import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';

const SCOPE_GLOBAL = 0;

const GLOBAL_MODULES = ['contacts', 'messaging', 'campaigns', 'settings'];

export const grantsScopedModule = team => team.permissions.some(p => !GLOBAL_MODULES.includes(p.split(':')[0]));

export default function usePermissions() {
    const user = useSelector(state => state.auth.user);
    const teams = useMemo(() => user?.teams ?? [], [user?.teams]);
    const isOwner = user?.isOwner ?? false;

    const hasAccess = useCallback(
        module =>
            isOwner ||
            teams.some(t => t.permissions.includes(`${module}:restricted`) || t.permissions.includes(`${module}:full`)),
        [isOwner, teams],
    );

    const hasFullAccess = useCallback(
        module => isOwner || teams.some(t => t.permissions.includes(`${module}:full`)),
        [isOwner, teams],
    );

    const hasFullAccessAt = useCallback(
        (module, locationId) =>
            isOwner ||
            (GLOBAL_MODULES.includes(module)
                ? teams.some(t => t.permissions.includes(`${module}:full`))
                : teams.some(
                      t =>
                          t.permissions.includes(`${module}:full`) &&
                          (t.scopeType === SCOPE_GLOBAL || t.locationIds?.includes(locationId)),
                  )),
        [isOwner, teams],
    );

    const hasFullAccessAtBrand = useCallback(
        (module, brandId) =>
            isOwner ||
            (GLOBAL_MODULES.includes(module)
                ? teams.some(t => t.permissions.includes(`${module}:full`))
                : teams.some(
                      t =>
                          t.permissions.includes(`${module}:full`) &&
                          (t.scopeType === SCOPE_GLOBAL || t.brandIds?.includes(brandId)),
                  )),
        [isOwner, teams],
    );

    const hasLocationAccess = useCallback(
        locId =>
            isOwner ||
            teams.some(t => grantsScopedModule(t) && (t.scopeType === SCOPE_GLOBAL || t.locationIds?.includes(locId))),
        [isOwner, teams],
    );

    const hasBrandAccess = useCallback(
        brandId =>
            isOwner ||
            teams.some(t => grantsScopedModule(t) && (t.scopeType === SCOPE_GLOBAL || t.brandIds?.includes(brandId))),
        [isOwner, teams],
    );

    const hasSettingsAccess = useCallback(
        () =>
            isOwner ||
            teams.some(t => t.permissions.includes('settings:restricted') || t.permissions.includes('settings:full')),
        [isOwner, teams],
    );

    const hasFullSettings = useCallback(
        () => isOwner || teams.some(t => t.permissions.includes('settings:full')),
        [isOwner, teams],
    );

    const hasGlobalScope = useMemo(
        () => isOwner || teams.some(t => grantsScopedModule(t) && t.scopeType === SCOPE_GLOBAL),
        [isOwner, teams],
    );

    const hasAnyBrandScope = useMemo(
        () => isOwner || hasGlobalScope || teams.some(t => grantsScopedModule(t) && t.brandIds?.length > 0),
        [isOwner, hasGlobalScope, teams],
    );

    const getWritableLocationIds = useCallback(
        module => {
            if (isOwner) return null; // null = all locations
            const perm = `${module}:full`;
            const isGlobalModule = GLOBAL_MODULES.includes(module);
            const ids = new Set();
            for (const t of teams) {
                if (!t.permissions.includes(perm)) continue;
                if (isGlobalModule || t.scopeType === SCOPE_GLOBAL) return null; // all locations
                if (t.locationIds) t.locationIds.forEach(id => ids.add(id));
            }
            return ids;
        },
        [isOwner, teams],
    );

    const getWritableBrandIds = useCallback(
        module => {
            if (isOwner) return null; // null = all brands
            const perm = `${module}:full`;
            const isGlobalModule = GLOBAL_MODULES.includes(module);
            const ids = new Set();
            for (const t of teams) {
                if (!t.permissions.includes(perm)) continue;
                if (isGlobalModule || t.scopeType === SCOPE_GLOBAL) return null; // all brands
                if (t.brandIds) t.brandIds.forEach(id => ids.add(id));
            }
            return ids;
        },
        [isOwner, teams],
    );

    return {
        hasAccess,
        hasFullAccess,
        hasFullAccessAt,
        hasFullAccessAtBrand,
        hasLocationAccess,
        hasBrandAccess,
        hasSettingsAccess,
        hasFullSettings,
        hasGlobalScope,
        hasAnyBrandScope,
        isOwner,
        getWritableLocationIds,
        getWritableBrandIds,
    };
}
